import {
  getTechnicalMember,
  MemberExplorerApiError,
  type MemberContract,
} from '@urbanik/api-client';
import { StatePanel, canonicalStates, formatDateTime } from '@urbanik/ui';
import { useEffect, useState } from 'react';
import { apiBase } from './api-base';
import './members.css';
type State = 'loading' | 'empty' | 'error' | 'forbidden' | 'success';
const canonical = (kind: Exclude<State, 'success'>) =>
  canonicalStates.find((item) => item.kind === kind)!;
export function MemberExplorerView() {
  const forced = new URLSearchParams(window.location.hash.split('?')[1]).get(
    'state',
  ) as State | null;
  const [state, setState] = useState<State>('loading');
  const [member, setMember] = useState<MemberContract | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (forced && forced !== 'success') {
      setState(forced);
      return;
    }
    const controller = new AbortController();
    setState('loading');
    getTechnicalMember(apiBase, controller.signal)
      .then((r) => {
        setMember(r.member);
        setState(r.member ? 'success' : 'empty');
      })
      .catch((e) => {
        if (controller.signal.aborted) return;
        if (e instanceof MemberExplorerApiError && e.kind === 'unauthorized') {
          window.location.hash = '#login';
          return;
        }
        setState(
          e instanceof MemberExplorerApiError && e.kind === 'forbidden' ? 'forbidden' : 'error',
        );
      });
    return () => controller.abort();
  }, [attempt, forced]);
  if (state !== 'success' || !member)
    return (
      <div className="shell-page members-page">
        <header className="page-heading">
          <div>
            <p className="kicker">M03.1 · Contrato</p>
            <h1>Explorador técnico de socio.</h1>
            <p>Lectura protegida del fixture persistido en PostgreSQL.</p>
          </div>
        </header>
        <StatePanel
          state={canonical(state === 'success' ? 'empty' : state)}
          onAction={() => setAttempt((v) => v + 1)}
        />
      </div>
    );
  return (
    <div className="shell-page members-page">
      <header className="page-heading">
        <div>
          <p className="kicker">M03.1 · Contrato persistente</p>
          <h1>Explorador técnico de socio.</h1>
          <p>Una única ficha de revisión; no es listado ni expediente operativo.</p>
        </div>
        <span className="surface-badge">PostgreSQL · v{member.version}</span>
      </header>
      <section className="member-card" aria-labelledby="member-name">
        <div>
          <p className="kicker">Fixture local M03.1</p>
          <h2 id="member-name">
            {member.givenNames} {member.familyNames}
          </h2>
          <span className="member-status">Activo</span>
        </div>
        <dl>
          <div>
            <dt>Cédula (texto)</dt>
            <dd>
              <code>{member.nationalId}</code>
            </dd>
          </div>
          <div>
            <dt>Teléfono principal (texto)</dt>
            <dd>{member.primaryPhone ?? 'No registrado'}</dd>
          </div>
          <div>
            <dt>Teléfono secundario (texto)</dt>
            <dd>{member.secondaryPhone ?? 'No registrado'}</dd>
          </div>
          <div>
            <dt>Correo</dt>
            <dd>{member.email ?? 'No registrado'}</dd>
          </div>
          <div>
            <dt>Identificador técnico</dt>
            <dd>
              <code>{member.id}</code>
            </dd>
          </div>
          <div>
            <dt>Actualizado</dt>
            <dd>{formatDateTime(member.updatedAt)}</dd>
          </div>
        </dl>
        <section aria-labelledby="history-title">
          <h3 id="history-title">Historial mínimo</h3>
          <ol>
            {member.history.map((h) => (
              <li key={h.id}>
                <strong>Activo</strong>
                <span>{h.reason}</span>
                <time dateTime={h.occurredAt}>{formatDateTime(h.occurredAt)}</time>
              </li>
            ))}
          </ol>
        </section>
      </section>
    </div>
  );
}
